import { useRef, useState } from "react";
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, Loader2, ArrowRight, ArrowLeft, AlertTriangle, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Shared bulk import dialog. Flow: Download template → Upload (drag & drop) → Map columns →
// Validate/Preview → Submit → Summary (with a downloadable failed-rows report). Works with every
// bulk endpoint that follows the core/bulk.py template→preview→bulk-import contract.
// Props:
//   entityLabel, templateUrl, previewUrl, importUrl  (importUrl may already include a path id)
//   testId, triggerLabel, onImported, extraFields (object appended to the import FormData),
//   trigger (custom trigger node instead of the default button)
export default function BulkImportDialog({ entityLabel, templateUrl, previewUrl, importUrl, testId = "bulk", triggerLabel = "Bulk import", onImported, extraFields, trigger }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("upload"); // upload | map | preview | done
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const label = (entityLabel || "record").toLowerCase();

  const reset = () => { setStep("upload"); setBusy(false); setDragOver(false); setFile(null); setPreview(null); setMapping({}); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const download = async () => {
    try {
      const res = await api.get(templateUrl, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${label.replace(/\s+/g, "_")}_import_template.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the template"); }
  };

  const onFileChosen = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post(previewUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
      setMapping(res.data.suggested_mapping || {});
      setStep("map");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the file");
      reset();
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFileChosen(f);
  };

  const fields = preview?.fields || [];
  const missingRequired = fields.filter((f) => f.required && !mapping[f.key]);
  const mappedFields = fields.filter((f) => mapping[f.key]);
  const unmapped = preview?.unmapped_columns || [];
  const sampleRows = preview?.sample_rows || [];

  const runImport = async () => {
    if (missingRequired.length) { toast.error(`Map required field: ${missingRequired.map((f) => f.label).join(", ")}`); return; }
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(clean));
      Object.entries(extraFields || {}).forEach(([k, v]) => fd.append(k, v));
      const res = await api.post(importUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data); setStep("done");
      const created = successCount(res.data);
      if (created > 0) { toast.success(`${created} ${label}${created === 1 ? "" : "s"} imported`); onImported?.(); }
      else if (res.data.failed > 0) toast.error("No rows imported — check the errors below");
      else toast.success("Import complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const successCount = (r) => r?.created ?? r?.created_pos ?? r?.created_transfers ?? r?.added ?? r?.resolved ?? 0;

  const downloadFailed = () => {
    const rows = result?.failed_rows || [];
    if (!rows.length) return;
    const keys = [...new Set(rows.flatMap((r) => Object.keys(r)))];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${label.replace(/\s+/g, "_")}_failed_rows.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const created = result ? successCount(result) : 0;
  const skipped = result?.skipped_duplicates || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid={`${testId}-import-btn`}>
            <Upload className="h-4 w-4 mr-1.5" />{triggerLabel}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" />Bulk import {label}s</DialogTitle>
          <DialogDescription>
            {step === "upload" && "Download the template, then drag & drop or choose an Excel (.xlsx) or CSV file — any column names work, you'll map them next."}
            {step === "map" && "Match your file's columns to the fields below. We've auto-matched what we could."}
            {step === "preview" && "Review the mapped rows below, then submit to import."}
            {step === "done" && "Import summary."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <Button variant="outline" className="w-full" onClick={download} data-testid={`${testId}-template-btn`}>
              <Download className="h-4 w-4 mr-1.5" />Download sample template (.xlsx)
            </Button>
            <label data-testid={`${testId}-dropzone`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg py-10 cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
              <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid={`${testId}-file-input`}
                onChange={(e) => onFileChosen(e.target.files?.[0])} disabled={busy} />
              {busy ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : <Upload className="h-6 w-6 text-muted-foreground" />}
              <span className="text-sm text-muted-foreground">{busy ? "Reading file…" : (dragOver ? "Drop the file to upload" : "Drag & drop, or click to choose a .xlsx / .csv file")}</span>
            </label>
          </div>
        )}

        {step === "map" && preview && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Badge variant="secondary" data-testid={`${testId}-detected-cols`}>{preview.columns.length} columns detected</Badge>
              <span className="truncate">{file?.name}</span>
            </div>
            {unmapped.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`${testId}-unmapped-warning`}>
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{unmapped.length} column{unmapped.length === 1 ? "" : "s"} weren't recognized (<span className="font-medium">{unmapped.join(", ")}</span>). Map them to a field below, or they'll be ignored on import.</span>
              </div>
            )}
            <div className="max-h-[300px] overflow-y-auto border border-border rounded-md divide-y divide-border">
              {fields.map((f) => (
                <div key={f.key} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`${testId}-map-row-${f.key}`}>
                  <span className="text-sm">{f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}</span>
                  <select
                    data-testid={`${testId}-map-${f.key}`}
                    value={mapping[f.key] || ""}
                    onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                    className={`h-8 w-48 rounded-md border bg-background px-2 text-xs ${f.required && !mapping[f.key] ? "border-destructive" : "border-border"}`}>
                    <option value="">— Skip / not in file —</option>
                    {preview.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-1">
              <Button variant="ghost" size="sm" onClick={() => reset()} data-testid={`${testId}-back-btn`}><ArrowLeft className="h-4 w-4 mr-1" />Choose another file</Button>
              <Button size="sm" onClick={() => setStep("preview")} disabled={missingRequired.length > 0} data-testid={`${testId}-preview-btn`}>
                Validate & preview<ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Showing the first {Math.min(sampleRows.length, 5)} mapped row{sampleRows.length === 1 ? "" : "s"} from <span className="font-medium">{file?.name}</span>. Unmapped columns are excluded.</div>
            <div className="max-h-[320px] overflow-auto border border-border rounded-md" data-testid={`${testId}-preview-table`}>
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>{mappedFields.map((f) => <th key={f.key} className="text-left font-medium px-2 py-1.5 whitespace-nowrap">{f.label}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sampleRows.slice(0, 5).map((row, i) => (
                    <tr key={i} data-testid={`${testId}-preview-row-${i}`}>
                      {mappedFields.map((f) => <td key={f.key} className="px-2 py-1.5 whitespace-nowrap">{String(row[mapping[f.key]] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between pt-1">
              <Button variant="ghost" size="sm" onClick={() => setStep("map")} data-testid={`${testId}-preview-back-btn`}><ArrowLeft className="h-4 w-4 mr-1" />Back to mapping</Button>
              <Button size="sm" onClick={runImport} disabled={busy} data-testid={`${testId}-submit-btn`}>
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1" />}Submit import
              </Button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-3" data-testid={`${testId}-summary`}>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge variant="secondary" data-testid={`${testId}-total`}>{result.total} rows</Badge>
              <span className="flex items-center gap-1 text-emerald-600" data-testid={`${testId}-created-count`}><CheckCircle2 className="h-4 w-4" />{created} added</span>
              {skipped > 0 && <span className="flex items-center gap-1 text-amber-600" data-testid={`${testId}-skipped-count`}><Copy className="h-4 w-4" />{skipped} duplicate{skipped === 1 ? "" : "s"} skipped</span>}
              {result.failed > 0 && <span className="flex items-center gap-1 text-destructive" data-testid={`${testId}-failed-count`}><XCircle className="h-4 w-4" />{result.failed} failed</span>}
            </div>
            {skipped > 0 && (
              <div className="max-h-32 overflow-y-auto border border-amber-200 rounded-md divide-y divide-amber-100 text-xs">
                {result.duplicate_rows.map((f, i) => (
                  <div key={i} className="px-3 py-1.5 flex justify-between gap-2" data-testid={`${testId}-duplicate-row-${i}`}>
                    <span className="text-muted-foreground">Row {f.row}{f.full_name ? ` · ${f.full_name}` : ""}</span>
                    <span className="text-amber-700 text-right">matches {f.existing_code || "existing"} ({f.match_type})</span>
                  </div>
                ))}
              </div>
            )}
            {result.failed > 0 && (
              <>
                <div className="max-h-40 overflow-y-auto border border-border rounded-md divide-y divide-border text-xs">
                  {result.failed_rows.map((f, i) => (
                    <div key={i} className="px-3 py-1.5 flex justify-between gap-2" data-testid={`${testId}-failed-row-${i}`}>
                      <span className="text-muted-foreground">Row {f.row}{(f.sku || f.vendor_name || f.name || f.supplier || f.product_code) ? ` · ${f.sku || f.vendor_name || f.name || f.supplier || f.product_code}` : ""}</span>
                      <span className="text-destructive text-right">{f.error}</span>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="w-full" onClick={downloadFailed} data-testid={`${testId}-download-failed-btn`}>
                  <Download className="h-4 w-4 mr-1.5" />Download failed rows (.csv)
                </Button>
              </>
            )}
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={reset} data-testid={`${testId}-import-more-btn`}>Import another file</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
